import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconEdit } from '~/ui/core/icons';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { EditableVersionNote } from '../EditableVersionNote';
import { VersionNoteLine } from '../VersionNoteLine';

export type RenderNoteSlotArgs = {
    readonly architectureId: string;
    readonly canEditNote: boolean;
    readonly isEditingNote: boolean;
    readonly item: JobItemDetails;
    readonly note: string | undefined;
    readonly onDisplayExpandedChange?: (expanded: boolean) => void;
    readonly onEnterEditMode: () => void;
    readonly onExitEditMode: () => void;
    readonly showInlineEditAction?: boolean;
    readonly versionNumber: number;
};

export const VersionNoteSlot: FunctionComponent<RenderNoteSlotArgs> = ({
    architectureId,
    canEditNote,
    isEditingNote,
    item,
    note,
    onDisplayExpandedChange,
    onEnterEditMode,
    onExitEditMode,
    showInlineEditAction = false,
    versionNumber,
}) => {
    const translate = useTranslate();
    const hasNote = is.nonEmptyString(note);

    if (canEditNote) {
        return (
            <span className={`flex w-full min-w-0 gap-xs ${isEditingNote ? 'items-stretch' : 'items-baseline'}`}>
                {!isEditingNote ? (
                    <span className="shrink-0 text-xs font-medium text-gray-900">Version comment:</span>
                ) : null}
                <span className="flex min-w-0 flex-1 items-baseline gap-xs">
                    <EditableVersionNote
                        actionsVariant="text"
                        architectureId={architectureId}
                        isEditing={isEditingNote}
                        itemId={item.id}
                        itemType={item.type}
                        layout={isEditingNote ? 'expanded-action-bar' : 'default'}
                        note={note}
                        version={versionNumber}
                        onDisplayExpandedChange={onDisplayExpandedChange}
                        onEnterEditMode={onEnterEditMode}
                        onExitEditMode={onExitEditMode}
                    />
                    {showInlineEditAction && hasNote && !isEditingNote ? (
                        <Button
                            aria-label={translate('pages.job-architecture-v2.version-note.edit')}
                            className="ml-auto shrink-0 !text-gray-700 hover:!text-black-900"
                            iconOnly={true}
                            size="sm"
                            type="button"
                            variant="tertiary"
                            onClick={onEnterEditMode}
                        >
                            <IconEdit height={14} width={14} />
                        </Button>
                    ) : null}
                </span>
            </span>
        );
    }
    if (is.nonEmptyString(note)) {
        return (
            <span className="flex min-w-0 items-start gap-xs">
                <span className="shrink-0 text-xs font-medium text-gray-900">Version comment:</span>
                <VersionNoteLine note={note} onExpandedChange={onDisplayExpandedChange} />
            </span>
        );
    }
    return null;
};
